import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import authorize from '../../utils/auth';
import AuthorizedResource from '../authorization/AuthorizedResource';
import DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER, { DS_PERMISSIONS_TOAST_SCOPE } from '../../controllers/directSubmissionPermissions';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import NoData from '../page/NoData';
import DirectSubmissionPermissionsTable from './DirectSubmissionPermissionsTable';
import ApplicationToast from '../inAppMessages/ApplicationToast';
import { toastActions } from '../inAppMessages/toastActions';
import Logger, { LOG_LEVELS } from '../../utils/logger';

const FORM_NAME = "DIRECT_SUBMISSION_PERMISSIONS_PAGE";
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

export const DirectSubmissionPermissionsPage = (props) => {
    const [toggleFilterPanel, setToggleFilterPanel] = useState(true);

    const allCompanyOptions = useMemo(() => props.allCompanies?.map((company) => {
        return {
            label: `${company.code} - ${company.name}`,
            value: company.id.toString()
        };
    }), [props.allCompanies]);

    useEffect(() => {
        props.onPageLoad(props.tenantId, props.iamTenantId, () => {
            log.error('Failed to load Direct Submission permissions');
        });

        return () => {
            log.clearAllDictionaryErrors();
        };
    }, []);

    const handleToggleFilterPanel = () => {
        setToggleFilterPanel(!toggleFilterPanel);
    };

    return (
        <div className="content-area" data-testid="ds-permissions-page">
            <s-container>
                <h1>
                    <LanguageStringResolver string='DSPermissions_Title' />
                </h1>
                <p className="mb30">
                    <LanguageStringResolver string='DSPermissions_PageDescription' />
                </p>

                <s-alert status="info" nodismiss={true} class="mb20" data-testid="ds-permission-update-info">
                    <LanguageStringResolver string='DSPermissions_PermissionUpdateInfo' />
                </s-alert>

                <s-box class="mb20" data-testid="ds-permission-types-panel">
                    <h3 className="margin-top-none margin-bottom-xs">
                        <LanguageStringResolver string='DSPermissions_PermissionTypes_Title' />
                    </h3>
                    <s-row>
                        <s-col span="6">
                            <p className="bold margin-bottom-none">
                                <LanguageStringResolver string='DSPermissions_Initiator_Title' />
                            </p>
                            <p className="margin-top-xs margin-bottom-none">
                                <LanguageStringResolver string='DSPermissions_Initiator_Description' />
                            </p>
                        </s-col>
                        <s-col span="6">
                            <p className="bold margin-bottom-none">
                                <LanguageStringResolver string='DSPermissions_Approver_Title' />
                            </p>
                            <p className="margin-top-xs margin-bottom-none">
                                <LanguageStringResolver string='DSPermissions_Approver_Description' />
                            </p>
                        </s-col>
                    </s-row>
                </s-box>

                {props.users && props.users.length > 0 ? (
                    <>
                        <s-row>
                            <s-col span="6">
                                <button
                                    className={toggleFilterPanel ? "toggle-button icon-leading mt10" : "secondary icon-leading mt10"}
                                    data-testid="filter-button"
                                    onClick={handleToggleFilterPanel}
                                >
                                    <s-icon name="filter" />
                                    <LanguageStringResolver string='FilterButton' />
                                </button>
                            </s-col>
                        </s-row>
                        <DirectSubmissionPermissionsTable
                            users={props.users}
                            companiesLookup={props.companiesLookup}
                            languages={props.languages}
                            toggleFilterPanel={toggleFilterPanel}
                            setToggleFilterPanel={setToggleFilterPanel}
                            allCompanyOptions={allCompanyOptions}
                            allCompanies={props.allCompanies}
                            companyVatNumbers={props.companyVatNumbers}
                            onLoadCompanyVatNumbers={props.onLoadCompanyVatNumbers}
                            onApplyChanges={props.onApplyChanges}
                            onGetUserDsScope={props.onGetUserDsScope}
                        />
                    </>
                ) : (
                    <NoData noDataText="DSPermissions_NoUsers" />
                )}
                <ApplicationToast
                    scope={DS_PERMISSIONS_TOAST_SCOPE}
                    testId="ds-permissions-toast"
                    onDismiss={props.onDismissToast}
                />
            </s-container>
        </div>
    );
};


export function mapStateToProps(state) {
    return {
        users: state.directSubmissionPermissions?.data,
        companiesLookup: state.directSubmissionPermissions?.companiesLookup,
        iamTenantId: state.authentication?.data?.iamData?.tenantId,
        error: state.directSubmissionPermissions?.error,
        languages: state.languages,
        allCompanies: state.directSubmissionPermissions?.allCompanies,
        companyVatNumbers: state.directSubmissionPermissions?.companyVatNumbers,
        tenantId: state.authentication?.data?.customerId
    };
}

export function mapDispatchersToProps(dispatch) {
    return {
        onPageLoad: (tenantId, iamTenantId, errorHandler) => {
            dispatch(DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER.doLoad(tenantId, iamTenantId, errorHandler));
        },
        onLoadCompanyVatNumbers: (companyIds) => {
            return dispatch(DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER.doLoadCompanyVatNumbers(companyIds));
        },
        onApplyChanges: (permissionChanges, scopeAssignments) => {
            return dispatch(DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER.doApplyChanges(permissionChanges, scopeAssignments));
        },
        onGetUserDsScope: (iamUserId) => {
            return dispatch(DIRECT_SUBMISSION_PERMISSIONS_CONTROLLER.doGetUserDsScope(iamUserId));
        },
        onDismissToast: (id) => {
            dispatch(toastActions.removeToast(id));
        }
    };
}

export default authorize(
    {
        name: 'DirectSubmissionPermissions',
        resource: connect(mapStateToProps, mapDispatchersToProps)(DirectSubmissionPermissionsPage)
    },
    AuthorizedResource,
    {
        showNotAuthorized: true
    }
);
